<template>
  <div>
    <div v-if="!messageSent && !showFeatureTable">
      <AlertBanner v-if="showErrorBanner" id="submit-error-alert" :status="0" class="mb-16 mt-8">
        {{ errorMessage }}
      </AlertBanner>
      <RecruitNotBasic
        v-if="!isBasic"
        :message="message"
        :recruitSitterTips="recruitSitterTips"
        :inputLabel="inputLabel"
        :messageSendLoading="messageSendLoading"
        :noJobsExist="noJobsExist"
        :jobPostReturnPath="jobPostReturnPath"
        :jobOptions="jobOptions"
        :selectedJob="selectedJob"
        :title="nonBasicUserTitle"
        @submit="runSendFlow"
        @input="message = $event"
        @update:message="message = $event"
        @update:selectedJob="selectedJob = $event"
      />
      <RecruitNoJobs
        v-if="isBasic && noJobsExist"
        :recruitSitterTipsForBasicUsers="recruitSitterTipsForBasicUsers"
        :titleForBasicUsers="titleForBasicUsers"
        :isPopupMode="isPopupMode"
        :upgradeMessage="upgradeMessage"
        :jobPostReturnPath="jobPostReturnPath"
      />
      <RecruitBasicJobs
        v-if="isBasic && !noJobsExist"
        :recruitSitterTipsForBasicUsers="recruitSitterTipsForBasicUsers"
        :titleForBasicUsers="titleForBasicUsers"
        :isPopupMode="isPopupMode"
        :upgradeMessage="upgradeMessage"
        :jobPostReturnPath="jobPostReturnPath"
        :jobOptions="jobOptions"
        :selectedJob="selectedJob"
        @update:selectedJob="selectedJob = $event"
        @recruitClicked="runSendFlow()"
      />
    </div>

    <div v-else-if="showFeatureTable" class="feature-table">
      <RecruitLimit @upgrade-click="setRecentlyViewedProviderId" />
    </div>

    <RecruitSitterSentView
      v-else-if="messageSent && selectedJobUuid"
      :recipientName="recipientName"
      :recipientUuid="recipientUuid"
      :isBasic="isBasic"
      :isPopupMode="isPopupMode"
      @continue-searching="$emit('continue-searching')"
    />

    <MessagesNewSent
      v-else-if="messageSent"
      :recipientUuid="recipientUuid"
      :isPopupMode="isPopupMode"
      @continue-searching="$emit('continue-searching')"
    />
  </div>
</template>

<script lang="ts">
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';
import RecruitLimit from '@/apps/messages/new/RecruitLimit.vue';
import RecruitNotBasic from '@/apps/messages/new/RecruitNotBasic.vue';
import RecruitBasicJobs from '@/apps/messages/new/RecruitBasicJobs.vue';
import RecruitNoJobs from '@/apps/messages/new/RecruitBasicNoJobs.vue';
import MessagesNewSent from './MessagesNewSent.vue';
import RecruitSitterSentView from './RecruitSitterSentView.vue';
import { CREATE_SENDBIRD_CHANNEL } from '@/graphql/messages/mutations';
import { CREATE_JOB_RECRUIT } from '@/graphql/recruit_sitter/mutation';
import { FETCH_PARENT_OPEN_JOBS, JobEdge, ViewerJobs } from '@/graphql/messages/queries';
import { Account } from '@/models/account';
import { sendSegmentEvent } from '@/lib/segment';
import { readMeta } from '@/lib/page';
import { setRecentlyViewedProviderId } from '@/lib/recentlyViewedProfile';
import { allowedJobCareTypesForSearchCareType } from '@/helpers/allowedJobCareTypes';
import type { SuccessPayload } from './types';

type RecruitSitterOptions = {
  emitSuccess?: boolean;
  manageLoading?: boolean;
};

export default {
  name: 'RecruitSitter',
  components: {
    AlertBanner,
    RecruitLimit,
    RecruitNotBasic,
    RecruitBasicJobs,
    RecruitNoJobs,
    MessagesNewSent,
    RecruitSitterSentView,
  },
  props: {
    account: {
      required: true,
      type: Account,
    },
    recipientUuid: {
      type: String,
      required: true,
    },
    recipientName: {
      type: String,
      required: true,
    },
    jobs: {
      type: Array as () => JobEdge[],
      default: null,
    },
    searchCareType: {
      type: String,
      default: '',
    },
    isPopupMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      message: '',
      messageSendLoading: false,
      messageSent: false,
      viewer: {} as ViewerJobs,
      selectedJob: '',
      showFeatureTable: false,
      showErrorBanner: false,
      errorMessage: '',
    };
  },
  computed: {
    inputLabel(): string {
      return this.recipientName ? `Start a chat with ${this.recipientName}` : 'Start a chat';
    },
    allowedJobCareTypes(): string[] {
      return allowedJobCareTypesForSearchCareType(this.searchCareType);
    },
    hasJobsProp(): boolean {
      return this.jobs !== null && this.jobs.length > 0;
    },
    filteredJobEdges(): JobEdge[] {
      const jobEdges = this.hasJobsProp ? this.jobs : this.viewer?.jobs?.edges || [];

      const allowed = this.allowedJobCareTypes;
      if (allowed.length === 0) {
        return jobEdges;
      }
      return jobEdges.filter((edge) => edge?.node?.careType && allowed.includes(edge.node.careType));
    },
    noJobsExist(): boolean {
      return this.filteredJobEdges.length === 0;
    },
    isBasic(): boolean {
      return this.account.accountType() === 'basic';
    },
    upgradeMessage(): string {
      return `Upgrade to Message ${this.recipientName}`;
    },
    titleForBasicUsers(): string {
      if (this.isPopupMode) {
        return '';
      }
      if (this.isBasic && this.noJobsExist) {
        return `Ask ${this.recipientName} to apply by creating your job`;
      }
      return `Ask ${this.recipientName} to apply for your job`;
    },
    nonBasicUserTitle(): string {
      if (!this.isPopupMode) {
        return `Start the chat with ${this.recipientName}`;
      }
      return '';
    },
    recruitSitterTipsForBasicUsers(): Array<string> {
      if (this.isBasic && this.noJobsExist) {
        return [`Easily detail your family's needs in one place, then share with ${this.recipientName} and other caregivers`];
      }
      return [`We'll let ${this.recipientName} know you're interested. If available, they'll apply to your job.`];
    },
    recruitSitterTips(): Array<string> {
      if (this.isPopupMode) {
        return [`Tell ${this.recipientName} why they're a good fit for your job`];
      }
      if (this.noJobsExist) {
        return [`Add a job to your message with ${this.recipientName} so they know about your schedule and needs`];
      }
      return [
        `Tell ${this.recipientName} why they're a good fit for your job`,
        'Ask about their availability to set up a call or an in-person interview',
      ];
    },
    jobPostReturnPath(): string {
      return encodeURIComponent(window.location.pathname + window.location.search);
    },
    formattedJobOptions(): { display: string; value: string }[] {
      return this.filteredJobEdges.map(({ node }) => {
        return { display: node.title, value: node.id };
      });
    },
    jobOptions(): { display: string; value: string | null }[] {
      if (!this.isBasic) {
        const otherJobOption = { display: "Don't send job", value: "Don't send job" };
        return [...this.formattedJobOptions, otherJobOption];
      }
      return [...this.formattedJobOptions];
    },
    selectedJobUuid(): string | null {
      if (this.selectedJob === "Don't send job") {
        return null;
      }
      return this.selectedJob;
    },
  },
  watch: {
    message(newVal: string): void {
      this.$emit('discard-check', newVal.length > 0);
    },
  },
  async mounted() {
    if (!this.hasJobsProp) {
      await this.fetchJobs();
    } else {
      const firstJob = this.filteredJobEdges[0]?.node?.id;
      this.selectedJob = firstJob || '';
    }
    this.fireRecruitSitterSegmentEvent();
  },
  methods: {
    shouldRecruitForSelectedJob(): boolean {
      return Boolean(this.selectedJobUuid);
    },
    shouldSendMessageAfterRecruitFlow(message: string): boolean {
      return message.length > 0 && (!this.shouldRecruitForSelectedJob() || !this.showFeatureTable);
    },
    emitSuccess(recruited: boolean, sentMessage: boolean): void {
      const payload: SuccessPayload = {
        recipientUuid: this.recipientUuid,
        recruited,
        sentMessage,
      };
      this.$emit('success', payload);
    },
    setRecentlyViewedProviderId(): void {
      if (this.isBasic) {
        setRecentlyViewedProviderId(this.recipientUuid);
      }
    },
    async runSendFlow(message: string = ''): Promise<void> {
      if (typeof message !== 'string') {
        message = '';
      }
      this.showErrorBanner = false;
      this.messageSendLoading = true;
      try {
        const recruited = this.shouldRecruitForSelectedJob() ? await this.recruitSitter() : false;
        const sentMessage = this.shouldSendMessageAfterRecruitFlow(message) ? await this.createSendbirdChannel(message) : false;
        if (!recruited && !sentMessage) {
          return;
        }

        this.emitSuccess(recruited, sentMessage);
        this.messageSent = true;
      } catch (error) {
        this.showErrorBanner = true;
        this.errorMessage = 'There was an error sending your message. Refresh and try again.';
      } finally {
        this.messageSendLoading = false;
      }
    },
    async recruitSitter(options: RecruitSitterOptions = {}): Promise<boolean> {
      const { emitSuccess = false } = options;
      try {
        await this.createJobRecruit();
        if (emitSuccess) {
          this.emitSuccess(true, false);
        }
        return true;
      } catch (error: any) {
        const graphqlError = error.graphQLErrors?.[0];
        if (graphqlError?.extensions?.error_code === 'max_recruit_error') {
          this.showFeatureTable = true;
          return false;
        }
        this.showErrorBanner = true;
        this.errorMessage = 'There was an error sending your message. Refresh and try again.';
        throw error;
      }
    },
    async createSendbirdChannel(message: string): Promise<boolean> {
      try {
        await this.$apollo.mutate({
          mutation: CREATE_SENDBIRD_CHANNEL,
          variables: {
            accountId: this.recipientUuid,
            message,
            session: readMeta('session'),
          },
        });
        this.messageSent = true;
        return true;
      } catch (error: any) {
        this.showErrorBanner = true;
        this.errorMessage = 'There was an error sending your message. Try again later.';
        throw error;
      }
    },
    async createJobRecruit(): Promise<void> {
      if (!this.selectedJobUuid) {
        return;
      }
      await this.$apollo.mutate({
        mutation: CREATE_JOB_RECRUIT,
        variables: {
          jobId: this.selectedJobUuid,
          providerId: this.recipientUuid,
        },
      });
    },
    async fetchJobs(): Promise<void> {
      if (this.hasJobsProp) {
        return;
      }
      try {
        const { data } = await this.$apollo.query({
          query: FETCH_PARENT_OPEN_JOBS,
          variables: { expiresAfter: new Date().toISOString() },
        });
        this.viewer = data.viewer;
        const firstJob = this.filteredJobEdges[0]?.node?.id;
        this.selectedJob = firstJob || '';
      } catch (error) {
        console.error('Error fetching jobs:', error);
      }
    },
    fireRecruitSitterSegmentEvent(): void {
      // Keep event name stable for existing analytics dependencies.
      sendSegmentEvent('Recruit Sitter to Job Flow Initiated', {
        sitter_user_id: this.recipientUuid,
        parent_user_id: this.account?.id,
        parent_subscription_status: this.account?.accountType(),
        has_open_job: !this.noJobsExist,
      });
    },
  },
};
</script>
